import React, { useEffect, useCallback, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import type { Photo } from '../lib/api';
import { getImageUrl } from '../lib/api';

interface ImageModalProps {
  image: Photo;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}

export function ImageModal({ image, onClose, onPrev, onNext }: ImageModalProps) {
  const [imageError, setImageError] = useState(false);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft' && onPrev) {
      onPrev();
    } else if (e.key === 'ArrowRight' && onNext) {
      onNext();
    }
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [handleKeyDown]);

  const handleImageError = () => {
    setImageError(true);
    console.error('Failed to load image:', getImageUrl(image));
  };

  return (
    <div 
      className="fixed inset-0 bg-black/95 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div 
        className="relative max-w-[90vw] max-h-[90vh] modal-animation"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors z-10"
        >
          <X className="w-8 h-8" />
        </button>

        {onPrev && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPrev();
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10"
          >
            <ChevronLeft className="w-12 h-12" />
          </button>
        )}
        
        {onNext && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNext();
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10"
          >
            <ChevronRight className="w-12 h-12" />
          </button>
        )}
        
        {imageError ? (
          <div className="text-center p-8 bg-white">
            <p>Nie udało się załadować zdjęcia</p>
            <button
              onClick={() => setImageError(false)}
              className="mt-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Spróbuj ponownie
            </button>
          </div>
        ) : (
          <img
            src={getImageUrl(image)}
            alt={image.name || 'Full size image'}
            className="max-w-full max-h-[90vh] object-contain"
            onError={handleImageError}
          />
        )}
      </div>
    </div>
  );
}